import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { Canal } from '@libs/entity-lib';
import { TranslationLibModule } from '@libs/translation';
import { addIcons } from 'ionicons';
import {
  alertCircleOutline,
  barbellOutline,
  cardOutline,
  checkmarkCircleOutline,
  chatbubbleEllipsesOutline,
  closeCircleOutline,
  constructOutline,
  refreshOutline,
  saveOutline,
  storefrontOutline,
  timeOutline,
} from 'ionicons/icons';
import { firstValueFrom } from 'rxjs';
import {
  MAINTENANCE_PAYMENT_CARD,
  MAINTENANCE_PAYMENT_PIX,
  MaintenanceApiService,
  MaintenanceChannel,
} from '../../services/maintenance-api.service';

/** Modelo editável do form do admin: inclui `time` (duração) que o GET não retorna. */
interface MaintenanceForm {
  active: boolean;
  message: string;
  time: string;
  expiresAt: string | null;
}

interface MaintenanceType {
  key: MaintenanceChannel;
  name: string;
  icon: string;
  description: string;
  /** Apps têm duração/countdown; PAYMENT é toggle manual sem expiração. */
  hasExpiry: boolean;
  status: ReturnType<typeof signal<MaintenanceForm>>;
}

import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonMenuButton,
  IonSpinner,
  IonTitle,
  IonToggle,
  IonToolbar,
} from '@ionic/angular/standalone';

@Component({
  selector: 'maintenance-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: true,
  imports: [
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonMenuButton,
    IonSpinner,
    IonTitle,
    IonToggle,
    IonToolbar,
    DatePipe,
    TranslationLibModule,
    FormsModule,
  ],
})
export class SettingsComponent {
  private readonly maintenanceService = inject(MaintenanceApiService);

  readonly lastUpdatedAt = signal<Date | null>(null);
  readonly loading = signal<boolean>(false);
  readonly savingKey = signal<string | null>(null);

  readonly maintenanceTypes = signal<MaintenanceType[]>([
    {
      key: Canal.SPORT_CENTER,
      name: 'Lessor',
      icon: 'storefront-outline',
      description: 'App do centro esportivo',
      hasExpiry: true,
      status: signal<MaintenanceForm>({
        active: false,
        message: 'No momento, estamos em manutenção.',
        time: '00:10',
        expiresAt: '',
      }),
    },
    {
      key: Canal.ATHLETE,
      name: 'Athlete',
      icon: 'barbell-outline',
      description: 'App do praticante',
      hasExpiry: true,
      status: signal<MaintenanceForm>({
        active: false,
        message: 'No momento, estamos em manutenção.',
        time: '00:10',
        expiresAt: '',
      }),
    },
    {
      key: MAINTENANCE_PAYMENT_PIX,
      name: 'Pagamento PIX',
      icon: 'card-outline',
      description: 'Bloqueia pagamento via PIX no checkout',
      hasExpiry: false,
      status: signal<MaintenanceForm>({
        active: false,
        message:
          'Pagamento via PIX temporariamente indisponível. Tente cartão ou tente novamente em instantes.',
        time: '',
        expiresAt: '',
      }),
    },
    {
      key: MAINTENANCE_PAYMENT_CARD,
      name: 'Pagamento Cartão',
      icon: 'card-outline',
      description: 'Bloqueia pagamento via cartão de crédito no checkout',
      hasExpiry: false,
      status: signal<MaintenanceForm>({
        active: false,
        message:
          'Pagamento via cartão temporariamente indisponível. Tente PIX ou tente novamente em instantes.',
        time: '',
        expiresAt: '',
      }),
    },
  ]);

  readonly modifiedTypes = signal<Record<string, boolean>>({});

  readonly totalCount = computed(() => this.maintenanceTypes().length);

  readonly activeCount = computed(
    () => this.maintenanceTypes().filter((t) => t.status().active).length
  );

  readonly availableCount = computed(() => this.totalCount() - this.activeCount());

  constructor() {
    addIcons({
      alertCircleOutline,
      barbellOutline,
      cardOutline,
      checkmarkCircleOutline,
      chatbubbleEllipsesOutline,
      closeCircleOutline,
      constructOutline,
      refreshOutline,
      saveOutline,
      storefrontOutline,
      timeOutline,
    });

    this.loadMaintenanceStatus();
  }

  async refresh(): Promise<void> {
    await this.loadMaintenanceStatus();
  }

  private async loadMaintenanceStatus(): Promise<void> {
    this.loading.set(true);
    try {
      for (const type of this.maintenanceTypes()) {
        try {
          const status = await firstValueFrom(this.maintenanceService.getStatus(type.key));
          type.status.set({
            ...type.status(),
            active: status.active,
            message: status.message ?? type.status().message,
            expiresAt: status.expiresAt ?? null,
          });
        } catch (error) {
          console.error(`Erro ao carregar status de ${type.key}:`, error);
        }
      }
      this.modifiedTypes.set({});
      this.lastUpdatedAt.set(new Date());
    } finally {
      this.loading.set(false);
    }
  }

  markAsModified(key: string): void {
    const current = this.modifiedTypes();
    this.modifiedTypes.set({ ...current, [key]: true });
  }

  updateActive(type: MaintenanceType, active: boolean): void {
    type.status.set({ ...type.status(), active });
    this.markAsModified(type.key);
  }

  updateMessage(type: MaintenanceType, message: string): void {
    type.status.set({ ...type.status(), message });
    this.markAsModified(type.key);
  }

  updateTime(type: MaintenanceType, time: string): void {
    type.status.set({ ...type.status(), time });
    this.markAsModified(type.key);
  }

  isModified(key: string): boolean {
    return !!this.modifiedTypes()[key];
  }

  async saveChanges(key: MaintenanceChannel, status: MaintenanceForm): Promise<void> {
    this.savingKey.set(key);
    try {
      await firstValueFrom(
        this.maintenanceService.setMaintenance(key, {
          active: status.active,
          message: status.message,
          time: status.time?.trim() || undefined,
        })
      );
      const current = this.modifiedTypes();
      this.modifiedTypes.set({ ...current, [key]: false });
      this.lastUpdatedAt.set(new Date());
    } catch (error) {
      console.error(`Erro ao salvar manutenção para ${key}:`, error);
    } finally {
      this.savingKey.set(null);
    }
  }
}
